/**
 * The ONE next-action ladder for a Work Order.
 *
 * WHY THIS EXISTS. The WO case file computed a guided next action; the
 * plan's work-order row computed "View RFQ" whenever status was
 * converted. Two answers to the same question, and the row's answer was
 * wrong the moment a quote existed — it pointed the operator BACKWARDS
 * at a superseded step. Worse, at 125 sites the plan list IS the work
 * queue: if each row only links to a record, the operator has to open
 * every case to find out what it needs. The row has to state the next
 * step and take you to where that step is done.
 *
 * INTAKE STYLE IS THE BRANCH THAT MATTERS. A work-order-fed (Corrigo)
 * client never receives an emailed quote — the quote is pasted into
 * CorrigoPro and its acceptance is recorded on the client's behalf. A
 * quote-fed client gets the quote by email and accepts it themselves.
 * Same case state, different next step, different destination. Anything
 * that resolves a next action without knowing the intake style will be
 * wrong for one of the two.
 *
 * WHAT THIS DOES NOT DO. It resolves the step, not the permission —
 * callers gate on `canEdit` themselves. And it deliberately returns a
 * DESTINATION rather than performing the action: posting to Corrigo,
 * sending a quote and recording acceptance all have their own surfaces
 * (with their own audit notes, recipient pickers and confirmations)
 * that must not be bypassed from a list row. The row's job is to name
 * the step and get you there in one click.
 */

export type WoNextActionKind =
  | "cancellation_requested"
  | "match"
  | "generate_rfq"
  | "price_rfq"
  | "generate_quote"
  | "post_to_corrigo"
  | "send_quote"
  | "record_acceptance"
  | "awaiting_client"
  | "awaiting_deposit"
  | "schedule_project"
  | "track_project"
  | "submit_invoice_corrigo"
  | "send_invoice"
  | "view_invoice"
  | "none";

export interface WoNextAction {
  kind: WoNextActionKind;
  /** Button label. Imperative — it names the step, not the record. */
  label: string;
  /** Where the step is performed. Null when there is nothing to do. */
  path: string | null;
  /** True when this is the case's live next step (render filled, not
   * outlined). False for passive states like awaiting the client, where
   * the affordance is a way to look rather than a thing to do. */
  primary: boolean;
  /** One-line "why this" for a tooltip / the case file's guided card. */
  hint?: string;
}

const NONE: WoNextAction = { kind: "none", label: "", path: null, primary: false };

/**
 * Resolve the next step for a WO. `wo` must carry the loaded chain
 * (convertedRfq + its quotes + their project/invoice) for the precise
 * answer; given a partial shape the ladder degrades to the coarse
 * intake-level step rather than lying about a later one.
 */
export function resolveWoNextAction(
  wo: any,
  opts?: { intakeStyle?: "work_order_fed" | "quote_fed" | null },
): WoNextAction {
  if (!wo) return NONE;

  // A cancelled case has no next step.
  if (wo.status === "rejected") return NONE;

  // CLIENT CANCELLATION REQUEST (Session 80) — resolved BEFORE every
  // other branch on purpose. Whatever this case was waiting on, an
  // outstanding request outranks it, because it decides whether the rest
  // of the ladder should happen at all: there is no point sending a
  // quote or dispatching a provider on work the client has asked us to
  // stop. The step is a DECISION, so it routes to the case file where
  // the banner's two actions live rather than performing anything.
  if (wo.cancellationRequestedAt) {
    return {
      kind: "cancellation_requested",
      label: "Cancellation requested",
      path: `/maintenance/work-orders/${wo.id}`,
      primary: true,
      hint: "The client asked us to cancel this. Decide before anything else moves.",
    };
  }

  const woFed =
    (opts?.intakeStyle ?? wo?.servicePlan?.intakeStyle ?? null) === "work_order_fed";

  const rfq = wo.convertedRfq ?? null;
  const quotes: any[] = Array.isArray(rfq?.quotes) ? rfq.quotes : [];
  const newestQuote = quotes.length
    ? [...quotes].sort((a: any, b: any) => (b?.sequence ?? 0) - (a?.sequence ?? 0))[0]
    : null;
  const acceptedQuote = quotes.find((q: any) => q?.status === 1) ?? null;
  const project = rfq?.project ?? newestQuote?.project ?? null;
  const invoice = newestQuote?.invoice ?? null;

  // The project's own ladder position (ProjectStatus.stepNumber): New 1 ·
  // Verified 2 · Site Ready 3 · Scheduled 4 · Complete 5 · Invoice Sent 6
  // · Closed 7 · Cancelled 0. "Complete" (5) is the gate: only once the
  // project is at least Complete is the crew's work actually done and the
  // invoice the live front. Before that, the next step lives ON the
  // project, no matter that an invoice record was generated early.
  const projectStep =
    project?.status?.stepNumber != null ? Number(project.status.stepNumber) : null;
  const projectComplete = projectStep != null && projectStep >= 5;
  // Session 65 item 5: an invoice RECORD often exists early (minted at
  // acceptance), but the invoice leg is not the live front until the
  // project's own "Invoice Sent" step (6) has fired. Between Complete (5)
  // and Invoice Sent (6) the operator's real next step is still ON the
  // project (create / send the invoice via the project transition), so
  // the ladder must keep pointing at the project-advance and must NOT
  // jump ahead to "Submit to Corrigo" just because an invoice row is
  // present. Only once the project has passed Invoice Sent does the
  // invoice/Corrigo branch own the next step.
  const invoiceStepFired = projectStep != null && projectStep >= 6;

  // Corrigo invoice submission is a REAL persisted event
  // (`corrigo_invoice_submission`, stamped onto the invoice as
  // `corrigoSubmittedAt` / statusName "Submitted" by the WO service). So
  // "Submit to Corrigo" is only the next step BEFORE that event; once it
  // is recorded, the submit is done and the case is waiting on Corrigo,
  // not on us. Without this the card kept saying "Submit to Corrigo"
  // forever, because nothing downstream cleared it.
  const invoiceSubmittedToCorrigo =
    !!invoice &&
    (!!(invoice as any).corrigoSubmittedAt ||
      String((invoice as any).statusName ?? "").toLowerCase() === "submitted");

  // ── Downstream first: the furthest-along record owns the next step ──

  // PROJECT GATE (fix): an invoice existing does NOT mean the job is done.
  // While the project is live but has not yet fired its "Invoice Sent"
  // step (6), the next step is to advance the PROJECT — even though an
  // invoice record may already exist (item 5). Between Complete (5) and
  // Invoice Sent (6) the project stepper's live action is to create /
  // send the invoice, so the ladder holds on track_project and the WO
  // surface drives the SAME project-advance the in-tab stepper uses
  // (Add Invoice comes before Submit-to-Corrigo). Only once Invoice Sent
  // has fired do the invoice/Corrigo branches below take over.
  if (project?.id && !invoiceStepFired && projectStep !== 0) {
    const beforeComplete = !projectComplete;
    return {
      kind: "track_project",
      label: beforeComplete ? "Advance project" : "Add invoice",
      path: `/maintenance/work-orders/${wo.id}?tab=project`,
      primary: true,
      hint: beforeComplete
        ? "The project is still in progress. Work it through its milestones on the project before the invoice is the next step."
        : "The work is complete. Create and send the invoice from the project step before submitting to Corrigo.",
    };
  }

  if (invoice?.id) {
    // Corrigo clients do not receive an emailed invoice either — it is
    // submitted through the Corrigo invoice flow, which records the
    // completion details and the posted-to-Corrigo audit row.
    if (woFed) {
      // Already submitted to Corrigo? Then the submit is DONE — the case
      // is waiting on Corrigo/payment, not on us. Don't re-ask.
      if (invoiceSubmittedToCorrigo) {
        return {
          kind: "view_invoice",
          label: "View invoice",
          path: `/invoices/${invoice.id}`,
          primary: false,
          hint: "Submitted to Corrigo. Awaiting approval and payment — nothing to do here until that lands.",
        };
      }
      return {
        kind: "submit_invoice_corrigo",
        label: "Submit to Corrigo",
        path: `/invoices/${invoice.id}`,
        primary: true,
        hint: "The work is invoiced. Submit it through CorrigoPro and mark it posted.",
      };
    }
    return {
      kind: "send_invoice",
      label: "View invoice",
      path: `/invoices/${invoice.id}`,
      primary: false,
      hint: "The work is invoiced. Open the invoice for send and payment status.",
    };
  }

  if (project?.id) {
    return {
      kind: "track_project",
      label: "View project",
      path: `/projects/${project.id}`,
      primary: false,
      hint: "The work is scheduled. Track delivery and completion on the project.",
    };
  }

  if (acceptedQuote?.id) {
    return {
      kind: "schedule_project",
      label: "Schedule work",
      path: `/quotes/${acceptedQuote.id}`,
      primary: true,
      hint: "The client accepted. Turn the accepted quote into scheduled work.",
    };
  }

  if (newestQuote?.id) {
    const status = Number(newestQuote.status);
    // 5 Superseded / 2 Denied / 4 Expired — the quote is dead, so the
    // live step is back on the case file (re-price, or a new version).
    if ([2, 4, 5].includes(status)) {
      return {
        kind: "generate_quote",
        label: "Re-quote",
        path: `/maintenance/work-orders/${wo.id}`,
        primary: true,
        hint: "This quote is no longer live. Generate a corrected one from the work order.",
      };
    }

    // Deposit outstanding. Checked BEFORE the sent/posted logic: a quote
    // that reached a deposit is by definition already in front of the
    // client, and falling through would label it "awaiting client —
    // nothing to do here until they respond", which is wrong. Money is
    // outstanding against a deadline and nothing starts until it lands.
    //
    // Only the PLAN-scoped list joins quotes, so these fields are
    // present there; the company-wide queue's partial shape carries no
    // quote and never reaches this branch.
    if (status === 6 && (newestQuote.priceLockDepositStatus ?? null) !== "received") {
      return {
        kind: "awaiting_deposit",
        label: "View quote",
        path: `/quotes/${newestQuote.id}`,
        primary: false,
        hint: "Deposit outstanding. Nothing starts until it lands \u2014 chase it if the deadline is close.",
      };
    }

    // "Has this quote moved past the send/post step" is answered
    // differently by intake style, and answering it wrong is what put
    // "Record acceptance" in front of a quote nobody had posted yet:
    //   Corrigo: the real signal is the posted-to-Corrigo submission
    //     (`corrigoQuoteSubmittedAt`, the same field the stage bars use).
    //     `effectiveStage` flips to "quote_sent" the moment ANY live
    //     quote exists - long before it is actually pasted into Corrigo -
    //     so it must NOT be used here, or the ladder skips the Post step.
    //   Quote-fed: the trunk has no send timestamp, so the coarse
    //     `effectiveStage === "quote_sent"` stays the best available
    //     signal (unchanged).
    const sent = woFed
      ? !!newestQuote.corrigoQuoteSubmittedAt
      : wo?.effectiveStage === "quote_sent";
    if (!sent) {
      // Generated but not yet in front of the client. THE step, and the
      // one that differs most by intake style.
      return woFed
        ? {
            kind: "post_to_corrigo",
            label: "Post to Corrigo",
            path: `/quotes/${newestQuote.id}?tab=corrigo`,
            primary: true,
            hint: "The quote is ready. Paste it into CorrigoPro, then mark it posted.",
          }
        : {
            kind: "send_quote",
            label: "Send quote",
            path: `/maintenance/work-orders/${wo.id}`,
            primary: true,
            hint: "The quote is ready. Send it to the client contact for approval.",
          };
    }

    // Sent and waiting. For a Corrigo client the acceptance arrives
    // outside JSCRM (a Corrigo message, a call), so SOMEONE has to
    // record it here — that is a real internal step, not idle waiting.
    return woFed
      ? {
          kind: "record_acceptance",
          label: "Accept on behalf of client",
          path: `/quotes/${newestQuote.id}?tab=corrigo`,
          primary: false,
          hint: "Posted to Corrigo. When the FM approves, record the acceptance on their behalf.",
        }
      : {
          kind: "awaiting_client",
          label: "View quote",
          path: `/quotes/${newestQuote.id}`,
          primary: false,
          hint: "Sent to the client. Nothing to do here until they respond.",
        };
  }

  if (rfq?.id) {
    // RFQ exists but no quote yet. Whether it is priced decides the
    // wording; both are worked on the case file.
    return {
      kind: "generate_quote",
      label: "Generate quote",
      path: `/maintenance/work-orders/${wo.id}`,
      primary: true,
      hint: "The RFQ is generated. Price it if needed, then cut the quote.",
    };
  }

  if (wo.matchStatus === "unmatched") {
    return {
      kind: "match",
      label: "Match site",
      path: `/maintenance/work-orders/${wo.id}`,
      primary: true,
      hint: "No resolved location yet. Match it so it can be priced against the right site.",
    };
  }

  return {
    kind: "generate_rfq",
    label: "Generate RFQ",
    path: `/maintenance/work-orders/${wo.id}`,
    primary: true,
    hint: "Generate an RFQ for this work order to price it.",
  };
}

/**
 * PRIORITY BAND for ordering a work-order list (Session 78, Jack).
 *
 * The plan's WO list arrives `receivedAt DESC` and is never re-sorted,
 * so a case that has been moving for three weeks sinks below intake
 * that landed this morning. At 125 sites that turns a work queue back
 * into a directory: the un-started "Needs RFQ" pile is the biggest and
 * the newest, so it sits on top of every case with a customer actually
 * waiting on it.
 *
 * The bands, and why in-flight beats un-started rather than the other
 * way round — which is the arguable call:
 *
 *   0 AT RISK    something has a date on it that has passed: an SLA
 *                complete-by on an unfinished case, or a deposit
 *                deadline gone by. These are already late.
 *   1 IN FLIGHT  a quote exists. There is a named customer mid-
 *                transaction, often with money outstanding. Dropping
 *                one of these costs a job; it does not just delay a
 *                batch.
 *   2 UN-STARTED match / RFQ / quote still to cut. Real work, and the
 *                operator's own, but it is a PILE — interchangeable,
 *                batched, and nothing is lost by doing it second. The
 *                rollup chips above the list are the way into it.
 *   3 SETTLED    nothing to do.
 *
 * Within a band the caller breaks ties on most-recent movement, so
 * "updated recently" surfaces without letting recency outrank lateness.
 */
export type WoPriorityBand = 0 | 1 | 2 | 3;

const IN_FLIGHT_KINDS: WoNextActionKind[] = [
  "post_to_corrigo",
  "send_quote",
  "record_acceptance",
  "awaiting_client",
  "awaiting_deposit",
  "schedule_project",
  "track_project",
  "submit_invoice_corrigo",
  "send_invoice",
  "view_invoice",
];

export function woPriorityBand(wo: any, next: WoNextAction): WoPriorityBand {
  const now = Date.now();

  // Session 80 — an outstanding cancellation request is band 0 on its
  // own, without needing an SLA breach underneath it. A client is
  // waiting on an answer they cannot get anywhere else, and every other
  // step on this case is possibly wasted work until someone decides.
  if (wo?.cancellationRequestedAt && wo?.status !== "rejected") return 0;

  // AT RISK. `caseClosed` (not the derived stage) is the done-ness test,
  // matching the rollup's past-SLA count — an invoiced-but-open case is
  // still late.
  const slaBlown =
    wo?.caseClosed !== true &&
    !!wo?.completeByAt &&
    new Date(wo.completeByAt).getTime() < now;

  const quotes: any[] = Array.isArray(wo?.convertedRfq?.quotes)
    ? wo.convertedRfq.quotes
    : [];
  const depositOverdue = quotes.some(
    (q: any) =>
      Number(q?.status) === 6 &&
      (q?.priceLockDepositStatus ?? null) !== "received" &&
      !!q?.priceLockExpiresAt &&
      new Date(q.priceLockExpiresAt).getTime() < now,
  );

  if (slaBlown || depositOverdue) return 0;
  if (IN_FLIGHT_KINDS.includes(next.kind)) return 1;
  if (next.kind === "none") return 3;
  return 2;
}

/**
 * Most recent movement on a case, for the within-band tiebreak. Falls
 * back to intake time when the row carries no update stamp.
 */
export function woLastMovedAt(wo: any): number {
  const candidates = [wo?.updatedAt, wo?.receivedAt, wo?.createdAt]
    .filter(Boolean)
    .map((d: any) => new Date(d).getTime())
    .filter((n: number) => !Number.isNaN(n));
  return candidates.length ? Math.max(...candidates) : 0;
}
